<script setup lang="ts">
import type { BlogPost } from '~/composables/useBlog'
import { logApiError } from '~/utils/logger'
import { sanitizeHtml } from '~/utils/sanitize'

// Composable
const { getPublishedPosts } = useBlog()

// State
const latestPost = ref<BlogPost | null>(null)
const loading = ref(true)

// Fetch latest published blog post
async function fetchLatestPost() {
  loading.value = true
  try {
    const { data } = await getPublishedPosts({
      sortBy: 'published_at',
      sortOrder: 'desc',
      limit: 1,
    })

    latestPost.value = data[0] || null
  }
  catch (error) {
    logApiError('/api/blog', error, { operation: 'fetchLatestPost' })
  }
  finally {
    loading.value = false
  }
}

function formatDate(dateString: string | null) {
  if (!dateString)
    return ''

  return new Date(dateString).toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// Lifecycle
onMounted(() => {
  fetchLatestPost()
})
</script>

<template>
  <section
    v-if="!loading && latestPost"
    id="nieuws"
    class="blog-preview-section py-16"
  >
    <VContainer>
      <!-- Section Header -->
      <VRow class="mb-8">
        <VCol
          cols="12"
          class="text-center"
        >
          <VChip
            color="primary"
            variant="tonal"
            class="mb-4"
          >
            <VIcon
              icon="bx-news"
              start
              size="18"
            />
            Nieuws
          </VChip>
          <h2 class="text-h3 mb-2">
            Laatste Nieuws
          </h2>
          <p class="text-body-1 text-medium-emphasis">
            Blijf op de hoogte van het laatste nieuws van 't SpeelFabrik
          </p>
        </VCol>
      </VRow>

      <!-- Latest Post Card -->
      <VRow justify="center">
        <VCol
          cols="12"
          lg="10"
        >
          <VCard
            class="blog-preview-card overflow-hidden"
            elevation="4"
          >
            <VRow no-gutters>
              <!-- Featured Image -->
              <VCol
                v-if="latestPost.featured_image_url"
                cols="12"
                md="5"
              >
                <VImg
                  :src="latestPost.featured_image_url"
                  :alt="latestPost.title"
                  height="100%"
                  min-height="300"
                  cover
                  class="h-100"
                />
              </VCol>

              <!-- Content -->
              <VCol
                cols="12"
                :md="latestPost.featured_image_url ? 7 : 12"
              >
                <VCardText class="pa-8 h-100 d-flex flex-column">
                  <!-- Meta -->
                  <div class="d-flex flex-wrap align-center gap-2 mb-4">
                    <VChip
                      v-if="latestPost.category"
                      color="primary"
                      size="small"
                      variant="tonal"
                    >
                      {{ latestPost.category }}
                    </VChip>
                    <span class="text-caption text-medium-emphasis">
                      <VIcon
                        icon="bx-calendar"
                        size="14"
                        class="me-1"
                      />
                      {{ formatDate(latestPost.published_at) }}
                    </span>
                  </div>

                  <!-- Title -->
                  <h3 class="text-h4 mb-4">
                    {{ latestPost.title }}
                  </h3>

                  <!-- Content Preview -->
                  <div
                    class="blog-content-preview flex-grow-1 mb-6"
                    v-html="sanitizeHtml(latestPost.content)"
                  />

                  <!-- Actions -->
                  <div class="d-flex flex-wrap gap-3">
                    <VBtn
                      color="primary"
                      variant="flat"
                      to="/nieuws"
                    >
                      <VIcon
                        icon="bx-news"
                        start
                      />
                      Alle Nieuws
                    </VBtn>
                  </div>
                </VCardText>
              </VCol>
            </VRow>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </section>
</template>

<style lang="scss" scoped>
/* stylelint-disable selector-pseudo-class-no-unknown */
.blog-preview-section {
  background-color: rgb(var(--v-theme-background));
}

.blog-preview-card {
  border-radius: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 12px 40px rgb(0 0 0 / 15%);
    transform: translateY(-4px);
  }
}

.blog-content-preview {
  position: relative;
  max-block-size: 200px;

  // Fade out effect
  &::after {
    position: absolute;
    background: linear-gradient(to bottom, transparent 0%, rgb(var(--v-theme-surface)) 100%);
    block-size: 60px;
    content: "";
    inline-size: 100%;
    inset-block-end: 0;
    inset-inline-start: 0;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-size: 1.1em;
    font-weight: 600;
    margin-block: 0.5em 0.25em;
  }

  :deep(p) {
    line-height: 1.6;
    margin-block-end: 0.75em;
  }

  :deep(ul),
  :deep(ol) {
    margin-block-end: 0.75em;
    padding-inline-start: 1.25em;
  }

  :deep(img) {
    display: none; // Hide images in preview
  }
}
</style>
